import type { LLMGenerationSettings } from "models/LLMs";

export interface KboAvailableModel {
    name: string;
    isThinkingModel: boolean;
    thinkingEffortLevels?: string[] | null;
}

export interface KboLlmSettings {
    mainAgent?: LLMGenerationSettings | null;
    powerPointAgent?: LLMGenerationSettings | null;
    salesforceAgent?: LLMGenerationSettings | null;
    reducer?: LLMGenerationSettings | null;
}

export interface KboEmbeddingSettings {
    toolSelectionModel?: string | null;
    soundClipModel?: string | null;
    soundClipMinSimilarity?: number | null;
    transcriptionModel?: string | null;
    transcriptionMinSimilarity?: number | null;
    knowledgeBaseModel?: string | null;
    knowledgeBaseMinSimilarity?: number | null;
    maxEmbeddingMessages?: number | null;
    maxEmbeddingTokens?: number | null;
}

export const KBO_TOOL_LOADING_MODES = {
    deferred: "deferred",
    eager: "eager",
} as const;

export type KboToolLoadingMode =
    (typeof KBO_TOOL_LOADING_MODES)[keyof typeof KBO_TOOL_LOADING_MODES];

export type KboPresetScope = "tenant" | "personal";

export interface KboSystemPromptSettings {
    organizationContext?: string | null;
}

export interface KboPromptsSettings {
    systemPromptTemplate?: string | null;
    authenticxBackground?: string | null;
    chatNameGenerationPrompt?: string | null;
    chatNameGenerationTemperature?: number | null;
    skillTestPrompt?: string | null;
}

export interface KboAnalysisPromptsSettings {
    singleTranscriptAnalysis?: string | null;
    singleTranscriptAnalysisTemperature?: number | null;
    multiTranscriptAnalysis?: string | null;
    multiTranscriptAnalysisTemperature?: number | null;
    additionalInsightsAnalysis?: string | null;
    additionalInsightsAnalysisTemperature?: number | null;
    conversationSummaryAnalysis?: string | null;
    conversationSummaryAnalysisTemperature?: number | null;
    additionalConversationSummaryAnalysis?: string | null;
    additionalConversationSummaryAnalysisTemperature?: number | null;
    deeperAnalysisPrompt?: string | null;
    deeperAnalysisTemperature?: number | null;
    singleResultDeeperAnalysis?: string | null;
    singleResultDeeperAnalysisTemperature?: number | null;
    summaryPrompt?: string | null;
    summaryTemperature?: number | null;
    singleResultSummaryPrompt?: string | null;
    singleResultSummaryTemperature?: number | null;
}

export interface KboChatHistorySettings {
    reduceAfterTokenCount?: number | null;
    reduceToMessageCount?: number | null;
}

export interface KboMemorySettings {
    defaultRecallLimit?: number | null;
    similarConversationMonths?: number | null;
    similarConversationDays?: number | null;
}

export interface KboSearchSettings {
    rerankingCandidateMultiplier?: number | null;
    defaultDocumentTypes?: string[] | null;
    suggestionSimilarityFloor?: number | null;
}

export interface KboAnalysisSettings {
    conversationAnalysis?: LLMGenerationSettings | null;
    conversationSummaryAnalysis?: LLMGenerationSettings | null;
    modelResultAnalysis?: LLMGenerationSettings | null;
    insightsTranscriptSampleSize?: number | null;
    insightsSummarySampleSize?: number | null;
    insightsMaxConversationCount?: number | null;
    insightsSummaryMaxConversationCount?: number | null;
    modelResultConversationsPerResult?: number | null;
    modelResultConversationsForSingleResult?: number | null;
}

export interface KboQuerySettings {
    sqlTimeoutSeconds?: number | null;
    settingsCacheTtlMinutes?: number | null;
}

export interface KboMessagesSettings {
    unableToProcessRequest?: string | null;
    noDataFound?: string | null;
    hierarchySynonyms?: string | null;
}

export interface KboToolConfiguration {
    functionName: string;
    enabled: boolean;
    groupName?: string | null;
    namespace?: string | null;
    /** When absent, the tool catalog default is used. */
    loadingMode?: KboToolLoadingMode | null;
    excludedProperties?: string[] | null;
}

export interface ToolDefinition {
    functionName: string;
    description?: string;
    pluginName?: string;
    namespace?: string | null;
    configurableProperties?: string[] | null;
    isInfrastructure?: boolean;
    defaultLoadingMode?: KboToolLoadingMode;
}

export interface KboSettings {
    version?: number;
    inheritFromTenantDefaults?: boolean | null;
    llm?: KboLlmSettings | null;
    embedding?: KboEmbeddingSettings | null;
    systemPrompt?: KboSystemPromptSettings | null;
    prompts?: KboPromptsSettings | null;
    analysisPrompts?: KboAnalysisPromptsSettings | null;
    chatHistory?: KboChatHistorySettings | null;
    memory?: KboMemorySettings | null;
    search?: KboSearchSettings | null;
    analysis?: KboAnalysisSettings | null;
    query?: KboQuerySettings | null;
    messages?: KboMessagesSettings | null;
    toolConfigurations?: KboToolConfiguration[] | null;
    toolNamespaceDescriptions?: Record<string, string> | null;
    selectedSkillNames?: string[] | null;
    activePresetName?: string | null;
    activePresetScope?: KboPresetScope | null;
    experimental?: Record<string, unknown> | null;
}

export interface KboSkillDefinition {
    name: string;
    description: string;
    toolNames: string[];
    toolCount: number;
}
